import React from "react";
import { Box, Typography, IconButton, Avatar } from "@mui/material";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import NewOnSaleProductCard from "../../Components/Product/NewOnSaleProductCard";
import Header from "../../Components/Common/Header";
import { motion } from "framer-motion";

const AssistantResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const products = location.state?.products || [];

  React.useEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType]);

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
  };

  if (!products.length) {
    return (
      <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
        <Header />
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <Avatar
              src={require("../../assets/images/login-handpik-logo_cropped.png")}
              sx={{
                width: 64,
                height: 64,
                mb: 3,
                mx: "auto",
                background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
                boxShadow: "0 8px 24px rgba(255, 140, 56, 0.3)",
                img: { objectFit: "contain", filter: "brightness(0) invert(1)", transform: "scale(0.6)" },
              }}
            />
          </motion.div>
          
          <Typography
            component={motion.h2}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            sx={{
              fontFamily: "Poppins-SemiBold, sans-serif",
              fontSize: "22px",
              color: "#1F1F1F",
              mb: 1,
            }}
          >
            No matches found
          </Typography>
          <Typography
            component={motion.p}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            sx={{
              fontFamily: "Poppins-Regular, sans-serif",
              fontSize: "15px",
              color: "#666",
              mb: 4,
              textAlign: "center",
              maxWidth: 320
            }}
          >
            The AI couldn't find exact matches for your request. Try tweaking your prompt or asking for something broader!
          </Typography>
          <Box
            component={motion.div}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={() => navigate(-1)}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: "100px",
              background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
              color: "#FFF",
              cursor: "pointer",
              fontFamily: "Poppins-SemiBold, sans-serif",
              fontSize: "14px",
              boxShadow: "0 4px 12px rgba(255, 140, 56, 0.3)",
              transition: "all 0.2s ease",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(255, 140, 56, 0.4)" },
              "&:active": { transform: "scale(0.96)" }
            }}
          >
            Go Back
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
      <Header />
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
        
        {/* Banner Section */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              p: { xs: 2, sm: 3 },
              borderRadius: "24px",
              background: "linear-gradient(135deg, rgba(255, 140, 56, 0.08) 0%, rgba(255, 155, 128, 0.02) 100%)",
              border: "1px solid rgba(255, 140, 56, 0.15)",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                background: "radial-gradient(circle, rgba(255,140,56,0.1) 0%, rgba(255,140,56,0) 70%)",
                borderRadius: "50%",
              }}
            />
            
            <IconButton 
              onClick={() => navigate(-1)} 
              sx={{ 
                bgcolor: "#FFF", 
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                "&:hover": { bgcolor: "#F5F5F5" },
                mr: { xs: 0, sm: 1 },
                flexShrink: 0
              }}
            >
              <ArrowBackIcon sx={{ color: "#333", fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>

            <Avatar
              src={require("../../assets/images/login-handpik-logo_cropped.png")}
              sx={{
                width: { xs: 44, sm: 52 },
                height: { xs: 44, sm: 52 },
                background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
                boxShadow: "0 4px 12px rgba(255, 140, 56, 0.3)",
                img: { objectFit: "contain", filter: "brightness(0) invert(1)", transform: "scale(0.65)" },
                flexShrink: 0
              }}
            />

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold, sans-serif",
                  fontSize: { xs: "16px", sm: "24px" },
                  color: "#1F1F1F",
                  lineHeight: 1.2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                Curated by AI
                <AutoAwesomeIcon sx={{ color: "#FF8C38", fontSize: { xs: 16, sm: 22 } }} />
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins-Medium, sans-serif",
                  fontSize: { xs: "12px", sm: "14px" },
                  color: "#666",
                  mt: 0.5
                }}
              >
                {products.length} handpicked {products.length === 1 ? "match" : "matches"} based on your request
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Product Grid */}
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="show"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 1.5, sm: 3 },
          }}
        >
          {products.map((product, idx) => (
            <motion.div key={idx} variants={itemVariants}>
              <NewOnSaleProductCard product={product} noColor />
            </motion.div>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AssistantResults;
